import type { TCurrentPlan } from '@/types/subscription';
import type { ReactNode } from 'react';

import { IconClockDollar } from '@tabler/icons-react';

import { useDateFormatter } from '@/hooks/useDateFormatter';
import { useFormatText } from '@/hooks/useFormatText';
import { formatCurrency } from '@/pages/common/settingTabs/subscription/helpers';

interface ICurrentPlanCardProps {
  currentPlan: TCurrentPlan | null;
  actions?: ReactNode;
}

const CurrentPlanCard = ({ currentPlan, actions }: ICurrentPlanCardProps) => {
  const { formatText } = useFormatText();
  const { formatDate } = useDateFormatter();

  const planName = currentPlan?.name ?? '-';
  const planPrice = currentPlan
    ? formatCurrency(currentPlan.amount, currentPlan.currency)
    : '-';
  const planStudents = currentPlan
    ? formatText('settings.subscription.students-count', {
        count: String(currentPlan.student_limit),
      })
    : '-';
  const planExpire = currentPlan ? formatDate(currentPlan.expiryDate) : '-';

  const cancelledAt =
    currentPlan?.isCancelled && currentPlan.cancelledAt
      ? currentPlan.cancelledAt
      : null;
  const cancelledBannerText = cancelledAt
    ? formatText('settings.subscription.cancelled-on', {
        date: formatDate(cancelledAt),
      })
    : '';

  return (
    <div className="mt-6 border border-neutral-light-200 rounded-xl">
      <div className="p-6">
        <div className="flex items-start justify-between">
          <div className="flex flex-col gap-1">
            <span className="text-base font-semibold text-neutral-primary">
              {formatText('settings.subscription.current-plan')}
            </span>
            <h3 className="text-2xl font-bold text-neutral-primary leading-8 tracking-tight">
              {planName}
            </h3>
          </div>
          {actions}
        </div>

        <div className="mt-6 flex gap-10 flex-wrap">
          <div className="flex flex-col gap-2">
            <span className="text-base font-semibold text-neutral-primary">
              {formatText('settings.subscription.price')}
            </span>
            <span className="text-base text-neutral-primary">{planPrice}</span>
          </div>
          <div className="flex flex-col gap-2">
            <span className="text-base font-semibold text-neutral-primary">
              {formatText('settings.subscription.no-of-students')}
            </span>
            <span className="text-base text-neutral-primary">
              {planStudents}
            </span>
          </div>
          <div className="flex flex-col gap-2">
            <span className="text-base font-semibold text-neutral-primary">
              {formatText('settings.subscription.expire-on')}
            </span>
            <span className="text-base text-neutral-primary">{planExpire}</span>
          </div>
        </div>

        {cancelledAt && (
          <div className="mt-5 flex items-center gap-3 bg-brand-light-50 rounded-lg px-4 py-3">
            <IconClockDollar size={20} className="text-brand-default" />
            <span className="text-sm text-neutral-primary">
              {cancelledBannerText}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default CurrentPlanCard;
